import { HTMLAttributes } from 'react'

import { AttributeSection, Size, SizeSelector } from '@syconium/little-miss-figgy'

import { usePrettySizeName } from '../../../app/_hooks/usePrettySizeName'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { SizeChartModalService } from '../../../brunswick/containers/SizeChartModalService'
import { SizeChart } from '../../pages/products/ProductPage/DetailsSection/SizesSection/SizeChart'

import * as Styles from './styles'

export type SizeAttributeProps = {
  title: string
  isInvalid?: boolean
  selected?: Size
  extended?: boolean
  accentColor?: string
  soldOut?: boolean
  sizeChart?: SizeChart
  sizes: Readonly<Size[]> | Size[]
  onSelect: (size: Size) => void
  className?: string
} & Omit<HTMLAttributes<HTMLDivElement>, 'onSelect'>

export type SizeSelectionTextProps = {
  soldOut: boolean
  children: React.ReactNode
}

/**
 * The text that is displayed with the attribute selector when a size is selected.
 */
function SizeSelectionText({ soldOut, children }: SizeSelectionTextProps) {
  const {
    detailsSection: { soldOutSectionTitleMessage },
  } = useTranslation()

  // Otherwise, return children + the sold out messaging
  return (
    <>
      {children} {soldOut && <Styles.SoldOut>{soldOutSectionTitleMessage}</Styles.SoldOut>}
    </>
  )
}

/**
 * An individual size attribute selector.
 */
export function SizeAttribute({
  title,
  isInvalid,
  selected,
  extended = false,
  soldOut = false,
  accentColor,
  sizeChart,
  sizes,
  onSelect,
  className,
  ...rest
}: SizeAttributeProps) {
  const { openSizeChartModal } = SizeChartModalService.useContainer()

  const {
    sizesSection: { sizeChartCTA },
  } = useTranslation()

  const prettySizeName = usePrettySizeName({
    id: selected?.shorthand,
    displayValue: selected?.shorthand,
  })

  return (
    <AttributeSection
      title={title}
      isInvalid={isInvalid}
      selection={
        selected && <SizeSelectionText soldOut={soldOut}>{prettySizeName}</SizeSelectionText>
      }
      endAdornment={
        <Styles.SizeChartButton onClick={() => sizeChart && openSizeChartModal(sizeChart)}>
          {sizeChartCTA}
        </Styles.SizeChartButton>
      }
      className={className}
      {...rest}
    >
      <SizeSelector
        accentColor={accentColor}
        minWidthStyle={extended ? 'lg' : 'md'}
        sizes={sizes}
        selected={selected}
        onSelectSize={onSelect}
        disableMobileHorizontalSwipe
      />
    </AttributeSection>
  )
}
